/*----------------------------------
- DOMAINS TITLE BLOCK
----------------------------------*/

/**
 * The page's title row on the marketing `PageHead` (`wp-head dm-head`, docs/mockups/website-domains.html): the H1 and
 * its lead from the search's SEO, the phone's "Browse by" hub links (`dm-chips`, below 1024px), then the listing's
 * controls (`wp-acts dm-acts`): Filters (below 1280px), "Sort · Relevance", Download, Create radar in ink.
 *
 * Every control opens authentication. Download and Create radar are member tools, Sort is a member tool the public
 * preview hands off (ADR-0029), and Filters only reveals the rail, which is itself a conversion affordance (ADR-0035).
 * Below 1024px the controls are one row that slides sideways, Filters first: the phone has no action bar
 * (2026-10-27), so this button opens the sheet there too.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { Badge } from '@uniquedomains/components/kit/badge/Badge';
import { Button, buttonClass } from '@uniquedomains/components/kit/button/Button';
import { PageHead } from '@uniquedomains/components/marketing/page-head/PageHead';

import Icon from '@/client/components/ui/icon/Icon';
import { DOMAINS_TITLE_ACTIONS_COPY } from '@/client/catalogs/landing/domains/pageCopy';
import { PUBLIC_PREVIEW_BROWSE_CHIPS_LABEL } from '@/client/catalogs/landing/domains/publicPreviewCopy';
import { PUBLIC_TABLE_SORT_LABEL } from '@/client/catalogs/landing/domains/publicTableColumns';

// Types
import type { DomainsBrowseChip } from '../lib/browseChips';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainsTitleBlockProps = {
    heroTitle: string;
    intro: string | null;
    /** Hub links the phone offers where the desktop keeps the filter rail. */
    browseChips: readonly DomainsBrowseChip[];
    /** Rules already applied to the search on screen, badged on the Filters opener. */
    activeFilterCount: number;
    onOpenFilters: () => void;
    onDownload: () => void;
    onSort: () => void;
    onCreateRadar: () => void;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** A hub link drawn as the system's soft 44px pill, as the mock's `dm-chips` row draws it. */
const CHIP_CLASS = buttonClass({ variant: 'soft', size: 'md' });

/*----------------------------------
- COMPONENT
----------------------------------*/

const DomainsTitleBlock = ({
    heroTitle,
    intro,
    browseChips,
    activeFilterCount,
    onOpenFilters,
    onDownload,
    onSort,
    onCreateRadar,
}: DomainsTitleBlockProps) => (
    <PageHead
        class="dm-head"
        title={heroTitle}
        lead={intro}
        actions={
            <>
                {/*
                  * The sheet's opener below 1280px; from 1280px the rail is a column with nothing to open, so the
                  * page sheet hides `dm-tabonly` there. Always in the tree rather than behind the viewport state:
                  * rendering a different number of controls on the server and on the client made Preact reconcile
                  * this row positionally, and it patched the neighbouring labels onto the wrong buttons.
                  */}
                <span class="dm-tabonly">
                    <Button
                        variant="soft"
                        size="md"
                        icon={<Icon name="sliders" size={15} strokeWidth={2} />}
                        data-testid="domains-filters-open-inline"
                        onClick={onOpenFilters}
                    >
                        {DOMAINS_TITLE_ACTIONS_COPY.filters}
                        {activeFilterCount > 0 ? <Badge size="sm" n={activeFilterCount} /> : null}
                    </Button>
                </span>
                {/* Ordering is a member tool: this opens authentication and never reorders the preview, so the
                    crawlable listing keeps one canonical order (ADR-0028). */}
                <Button
                    variant="soft"
                    size="md"
                    iconRight={<Icon name="chevron-down" size={15} strokeWidth={2} />}
                    data-testid="domains-sort"
                    onClick={onSort}
                >
                    {DOMAINS_TITLE_ACTIONS_COPY.sortPrefix} · {PUBLIC_TABLE_SORT_LABEL}
                </Button>
                <Button
                    variant="soft"
                    size="md"
                    icon={<Icon name="download" size={15} strokeWidth={2} />}
                    data-testid="domains-download"
                    onClick={onDownload}
                >
                    {DOMAINS_TITLE_ACTIONS_COPY.download}
                </Button>
                <Button
                    variant="ink"
                    size="md"
                    icon={<Icon name="radar" size={15} strokeWidth={2} />}
                    data-testid="domains-create-radar"
                    onClick={onCreateRadar}
                >
                    {DOMAINS_TITLE_ACTIONS_COPY.createRadar}
                </Button>
            </>
        }
    >
        {/*
          * Phone only (`dm-chips`, shown below 1024px by the page sheet), where the filter rail is a sheet: plain links
          * to crawlable hubs, so nothing here reorders or re-queries the preview below (ADR-0029).
          */}
        {browseChips.length ? (
            <nav class="dm-chips" aria-label={PUBLIC_PREVIEW_BROWSE_CHIPS_LABEL}>
                {browseChips.map((chip) => (
                    <a key={chip.href} href={chip.href} class={CHIP_CLASS}>
                        {chip.label}
                    </a>
                ))}
            </nav>
        ) : null}
    </PageHead>
);

export default DomainsTitleBlock;
